/**
 * The line under your character's name in the room: "heads down", "back at two", "on the checkout".
 *
 * Yours to write. DevPulse never writes it for you — a status a machine invents is a guess with
 * somebody's name on it — and it stays until you change it, because a line that expires quietly is
 * worse than no line at all.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageCircle } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'

const MAX = 60

export function StatusPhrase() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [touched, setTouched] = useState(false)

  const { data } = useQuery({ queryKey: ['my-status'], queryFn: api.room.status, retry: false })

  useEffect(() => {
    if (!touched) setText(data?.text ?? '')
  }, [data, touched])

  const save = useMutation({
    mutationFn: (value: string) => api.room.setStatus(value),
    onSuccess: () => {
      setTouched(false)
      void queryClient.invalidateQueries({ queryKey: ['my-status'] })
      for (const key of ['bug-hunt', 'room-activity']) void queryClient.invalidateQueries({ queryKey: [key] })
    },
  })

  return (
    <form
      className="stack stack--tight"
      onSubmit={(event) => {
        event.preventDefault()
        if (!save.isPending) save.mutate(text.trim())
      }}
    >
      <label className="field">
        <span className="field__label">{t('status.title')}</span>
        <div className="row row--tight">
          <MessageCircle size={14} aria-hidden className="muted" />
          <input
            className="input"
            value={text}
            onChange={(event) => {
              setTouched(true)
              setText(event.target.value)
            }}
            maxLength={MAX}
            placeholder={t('status.placeholder')}
            aria-label={t('status.title')}
          />
          <button type="submit" className="btn btn--sm btn--primary" disabled={save.isPending || (!touched && text === (data?.text ?? ''))}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>
          {text && (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => {
                setText('')
                save.mutate('')
              }}
            >
              {t('status.clear')}
            </button>
          )}
        </div>
        <span className="field__hint">
          {t('status.hint')}
          {data?.setAt && ` — ${t('status.setAt', { when: formatRelative(data.setAt, locale) })}`}
        </span>
      </label>
    </form>
  )
}
